// src/components/OnboardingGate.js
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { signOut } from 'firebase/auth';
import Auth from './Auth';
import ProfileForm from './ProfileForm/ProfileForm';
import Button from './Button';
import { AppIcon } from './icons';
import { auth } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { useCurrentUserProfile } from '../contexts/CurrentUserProfileContext';
import { getProfileSubmitErrorKey, isProfileComplete, mergeProfileDefaults } from '../utils/profile';

const LoadingScreen = () => (
  <div className="loading-screen">
    <AppIcon size={120} color="var(--text-primary)" className="loading-logo" aria-label="App Icon" />
  </div>
);

const ErrorScreen = ({ message, onRetry }) => (
  <div className="error-screen">
    <p>{message}</p>
    {onRetry ? (
      <Button onClick={onRetry} variant="primary" size="medium">
        Retry
      </Button>
    ) : null}
  </div>
);

/**
 * Centralized routing logic for auth/onboarding flow.
 * Renders exactly one of: Auth, Loading, ProfileForm, or App.
 * Prevents blank ProfileForm by waiting for server-confirmed data.
 */
const OnboardingGate = ({ children }) => {
  const { t } = useTranslation();
  const authState = useAuth();
  const profileState = useCurrentUserProfile();

  // Memoize defaults to prevent form resets on re-render
  const createProfileDefaults = useMemo(() => mergeProfileDefaults(), []);
  const profile = profileState.profile;
  const existingProfileDefaults = useMemo(
    () => mergeProfileDefaults(profile ?? undefined),
    [profile]
  );

  const handleCancel = async () => {
    try {
      await signOut(auth);
    } catch (error) {
      console.error('Failed to sign out during onboarding:', error);
    }
  };

  const handleSubmit = async (values, originalUsername, profilePicture) => {
    await profileState.updateCompleteUserProfile(values, originalUsername, profilePicture);
  };

  if (authState.status === 'loading') {
    return <LoadingScreen />;
  }

  if (authState.status === 'error') {
    return <ErrorScreen message={t('auth.auth_state_error', 'Authentication failed. Please reload.')} onRetry={() => window.location.reload()} />;
  }

  if (authState.status === 'signedOut') {
    return <Auth onAuthSuccess={() => {}} />;
  }

  // Wait for any profile data (cache or server) before deciding
  const waitingForProfile =
    profileState.status === 'loading' ||
    profileState.status === 'idle' ||
    (!profileState.hasReceivedSnapshot && profileState.status !== 'error');

  if (waitingForProfile) {
    return <LoadingScreen />;
  }

  if (profileState.status === 'error') {
    return <ErrorScreen message={t('profile.profile_load_error')} onRetry={() => window.location.reload()} />;
  }

  if (profileState.status === 'missing') {
    return (
      <ProfileForm
        mode="create"
        initialValues={createProfileDefaults}
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        pending={profileState.saving}
        submitErrorKey={getProfileSubmitErrorKey(profileState.saveError)}
      />
    );
  }

  const complete = isProfileComplete(profile);

  if (!complete) {
    return (
      <ProfileForm
        mode="edit"
        initialValues={existingProfileDefaults}
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        pending={profileState.saving}
        submitErrorKey={getProfileSubmitErrorKey(profileState.saveError)}
      />
    );
  }

  return <>{children}</>; // Main app
};

export default OnboardingGate;
